import { endOfDay, format, startOfDay } from "date-fns";
import { AlertCircle, CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { useFormContext } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { AnnouncementType } from "@classview/shared-types";

import { AddAnnouncementFormData } from "../types/add-announcement-schema";

import { OrganisationsSelectElement } from "./organisations-select.element";

export function AnnouncementElement() {
  const form = useFormContext<AddAnnouncementFormData>();
  const announcementTypes = Object.values(AnnouncementType).filter(
    (type) =>
      type !== AnnouncementType.URGENT && type !== AnnouncementType.WARNING,
  );

  const handleDateRangeChange = (rangeEvent: DateRange | undefined) => {
    if (!rangeEvent || !rangeEvent.from) {
      // @ts-expect-error - date is optional
      form.setValue("date", undefined);
      return;
    }

    form.setValue("date", {
      from: startOfDay(rangeEvent.from),
      to: rangeEvent.to ? endOfDay(rangeEvent.to) : endOfDay(rangeEvent.from),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-row gap-4">
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem className="flex flex-1 flex-col">
              <FormLabel isRequired>Announcement Type</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={form.formState.isSubmitting}
              >
                <FormControl>
                  <SelectTrigger
                    className={cn(
                      "capitalize",
                      !field.value && "text-muted-foreground",
                    )}
                  >
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {announcementTypes.map((type) => (
                    <SelectItem key={type} value={type} className="capitalize">
                      {type.toLowerCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem className="flex flex-1 flex-col">
              <FormLabel isRequired>Dates to be shown</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "pl-3 text-left font-normal",
                        !field.value && "text-muted-foreground",
                      )}
                    >
                      {field.value ? (
                        field.value.to ? (
                          <>
                            {format(field.value.from, "PP")} -{" "}
                            {format(field.value.to, "PP")}
                          </>
                        ) : (
                          format(field.value.from, "PP")
                        )
                      ) : (
                        <span>Select a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={field.value}
                    onSelect={handleDateRangeChange}
                    captionLayout="dropdown"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <OrganisationsSelectElement />
      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel isRequired>Title</FormLabel>
            <FormControl>
              <Input
                {...field}
                autoComplete="off"
                placeholder="Enter your announcement title..."
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <FormLabel isRequired>Message</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Enter a message..." />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {form.formState.errors.root && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unexpected error</AlertTitle>
          <AlertDescription>
            {form.formState.errors.root.message}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
